import React, { useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { API_BASE } from "@/App";
import {
  Calculator, Clock, Target, Lightbulb, TrendingUp, AlertCircle,
  Zap, CheckCircle, XCircle, BookOpen, ArrowRight
} from "lucide-react";

// Question Types
const QUESTION_TYPES = [
  { label: "Quantitative Aptitude", value: "quant", icon: "🔢" },
  { label: "Logical Reasoning", value: "reasoning", icon: "🧩" },
  { label: "Data Interpretation", value: "di", icon: "📊" },
  { label: "Reading Comprehension", value: "rc", icon: "📖" },
  { label: "Decision Making", value: "decision", icon: "⚖️" }
];

// PYQ Safety Signals
const PYQ_SIGNALS = {
  high: { label: "High PYQ-confidence", color: "bg-green-100 text-green-800 border-green-300", icon: "🟢" },
  medium: { label: "Medium PYQ-risk", color: "bg-yellow-100 text-yellow-800 border-yellow-300", icon: "🟡" },
  trap: { label: "PYQ trap-heavy", color: "bg-red-100 text-red-800 border-red-300", icon: "🔴" }
};

const CSATQuestionSolver = ({ isPro, onBack, initialQuestion = "", initialType = "quant" }) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [question, setQuestion] = useState(initialQuestion);
  const [questionType, setQuestionType] = useState(initialType);
  const [solution, setSolution] = useState(null);

  const handleSolveQuestion = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API_BASE}/csat/solve`, {
        question,
        question_type: questionType
      }, {
        timeout: 120000  // 2 minutes timeout for AI processing
      });
      setSolution(data);
      toast({ description: "CSAT solution generated!" });
    } catch (err) {
      const msg = err?.response?.data?.detail;
      if (err?.response?.status === 403 && msg) {
        setSolution({ 
          access_level: "FREE", 
          answer: "Answer available",
          paywall_message: msg 
        });
      } else {
        toast({ variant: "destructive", description: msg || "Could not solve question." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setSolution(null);
    onBack();
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-6xl mx-auto px-6 py-12 space-y-8">
        <div className="flex items-center justify-between">
          <Button variant="outline" className="border-slate-200" onClick={handleBack}>← Back to CSAT Suite</Button>
          <Badge variant="outline" className={`px-4 py-2 text-sm ${isPro ? 'border-slate-900 text-slate-900' : 'border-slate-300 text-slate-500'}`}>{isPro ? "PRO" : "FREE"}</Badge>
        </div>

        {/* Header - CLARIAS Style */}
        <div className="pb-6 border-b border-slate-100">
          <h1 className="text-2xl font-semibold text-slate-900">Question Solver</h1>
          <p className="text-slate-500">
            Elimination-first solutions • PYQ intelligence • Time estimates
          </p>
        </div>

        <Card className="border-slate-200 bg-white">
          <CardContent className="p-6">
            <form className="space-y-6" onSubmit={handleSolveQuestion}>
              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Question Type</label>
                <div className="flex flex-wrap gap-2">
                  {QUESTION_TYPES.map((type) => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={questionType === type.value ? "default" : "outline"}
                      size="sm"
                      onClick={() => setQuestionType(type.value)}
                      className={questionType === type.value ? "bg-slate-900 hover:bg-slate-800" : "border-slate-200"}
                    >
                      {type.icon} {type.label}
                    </Button>
                  ))}
                </div>
              </div>

              <div className="space-y-3">
                <label className="text-sm font-medium text-slate-700">Question</label>
                <Textarea
                  rows={6}
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  placeholder="Paste your CSAT question here (including options if applicable)..."
                  required
                  className="text-sm resize-none border-2 focus:border-slate-400"
                />
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 hover:bg-slate-800 text-white font-medium py-3"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Analyzing... (may take up to a minute)
                  </span>
                ) : "Solve Question"}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Loading State Message */}
        {loading && (
          <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50">
            <CardContent className="p-6 text-center">
              <div className="flex flex-col items-center gap-3">
                <svg className="animate-spin h-8 w-8 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 714 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <h3 className="text-lg font-semibold text-blue-900">CLARIAS is analyzing your question...</h3>
                <p className="text-sm text-blue-700">
                  {questionType === 'decision' && "Evaluating scenario, mapping stakeholders, and applying decision frameworks..."}
                  {questionType === 'rc' && "Analyzing passage, identifying key concepts, and breaking down questions..."}
                  {questionType === 'quant' && "Calculating multiple solution methods and identifying shortcuts..."}
                  {questionType === 'reasoning' && "Mapping logical patterns and building step-by-step reasoning..."}
                  {questionType === 'di' && "Analyzing data, extracting insights, and calculating solutions..."}
                </p>
                <p className="text-xs text-blue-600">This may take 30-60 seconds for comprehensive analysis</p>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Solution Display */}
        {solution && (
          <div className="space-y-5">
            {/* Enhanced RC with Multiple Questions */}
            {solution.is_multiple_questions && solution.questions && (
              <>
                {/* Passage Analysis */}
                {solution.passage_analysis && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">📖</div>
                        <h3 className="text-lg font-bold text-purple-900 uppercase">Passage Analysis</h3>
                      </div>
                      <div className="space-y-4">
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <p className="text-sm font-semibold text-purple-800 mb-1">Central Theme:</p>
                          <p className="text-slate-700">{solution.passage_analysis.central_theme}</p>
                        </div>
                        {solution.passage_analysis.author_tone && (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-purple-100 text-purple-800">Tone: {solution.passage_analysis.author_tone}</Badge>
                          </div>
                        )}
                        {solution.passage_analysis.key_points && (
                          <div className="bg-white rounded-lg p-4 border border-purple-200">
                            <p className="text-sm font-semibold text-purple-800 mb-2">Key Points:</p>
                            <ul className="space-y-1">
                              {solution.passage_analysis.key_points.map((point, idx) => (
                                <li key={idx} className="text-sm text-slate-700 flex items-start gap-2">
                                  <span className="text-purple-500">•</span> {point}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {solution.passage_analysis.scope_boundaries && (
                          <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                            <p className="text-sm text-red-800">
                              <span className="font-semibold">⚠️ Out of Scope:</span> {solution.passage_analysis.scope_boundaries}
                            </p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Each Question Analysis */}
                {solution.questions.map((q, qIdx) => (
                  <Card key={qIdx} className="border-slate-200 bg-white shadow-sm">
                    <CardContent className="p-6">
                      {/* Question Header */}
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="bg-indigo-600 text-white rounded-full w-10 h-10 flex items-center justify-center font-bold text-lg">
                            Q{q.question_number || qIdx + 1}
                          </div>
                          <div>
                            <p className="text-sm text-slate-500">{q.question_text || `Question ${qIdx + 1}`}</p>
                            <div className="flex gap-2 mt-1">
                              <Badge className="bg-slate-100 text-slate-700">{q.difficulty || "Medium"}</Badge>
                              <Badge className="bg-blue-100 text-blue-700">⏱️ {q.time_estimate || "60s"}</Badge>
                            </div>
                          </div>
                        </div>
                        <div className="bg-green-600 text-white px-4 py-2 rounded-lg font-bold text-xl">
                          {q.correct_answer}
                        </div>
                      </div>

                      {/* Elimination Analysis */}
                      {q.elimination_analysis && (
                        <div className="space-y-3 mb-4">
                          <p className="text-sm font-semibold text-slate-700 uppercase">Option-by-Option Analysis:</p>
                          <div className="grid md:grid-cols-2 gap-3">
                            {Object.entries(q.elimination_analysis).map(([opt, analysis]) => {
                              const isCorrect = analysis.status === "correct";
                              return (
                                <div 
                                  key={opt} 
                                  className={`rounded-lg p-4 border-2 ${
                                    isCorrect 
                                      ? "bg-green-50 border-green-300" 
                                      : "bg-red-50 border-red-200"
                                  }`}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    <span className={`font-bold text-lg ${isCorrect ? "text-green-700" : "text-red-700"}`}>
                                      {isCorrect ? "✓" : "✗"} Option {opt}
                                    </span>
                                    {analysis.trap_type && !isCorrect && (
                                      <Badge className="bg-red-100 text-red-700 text-xs">{analysis.trap_type}</Badge>
                                    )}
                                  </div>
                                  <p className={`text-sm ${isCorrect ? "text-green-800" : "text-red-800"}`}>
                                    {typeof analysis === 'string' ? analysis : analysis.reason}
                                  </p>
                                  {isCorrect && analysis.passage_evidence && (
                                    <div className="mt-2 p-2 bg-green-100 rounded border border-green-300">
                                      <p className="text-xs text-green-700">
                                        <span className="font-semibold">📌 Evidence:</span> {analysis.passage_evidence}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}

                      {/* Solution Approach */}
                      {q.solution_approach && (
                        <div className="bg-indigo-50 rounded-lg p-4 border border-indigo-200 mb-3">
                          <p className="text-sm font-semibold text-indigo-800 mb-1">💡 Solution Approach:</p>
                          <p className="text-sm text-indigo-900">{q.solution_approach}</p>
                        </div>
                      )}

                      {/* Topper Shortcut & Common Mistake */}
                      <div className="grid md:grid-cols-2 gap-3">
                        {q.topper_shortcut && (
                          <div className="bg-blue-50 rounded-lg p-3 border border-blue-200">
                            <p className="text-xs font-semibold text-blue-800">⚡ Topper Shortcut:</p>
                            <p className="text-sm text-blue-900">{q.topper_shortcut}</p>
                          </div>
                        )}
                        {q.common_mistake && (
                          <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                            <p className="text-xs font-semibold text-amber-800">⚠️ Common Mistake:</p>
                            <p className="text-sm text-amber-900">{q.common_mistake}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {/* Overall Strategy */}
                {solution.overall_strategy && (
                  <Card className="border-teal-200 bg-teal-50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-teal-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">🎯</div>
                        <h3 className="text-lg font-bold text-teal-900 uppercase">Overall RC Strategy</h3>
                      </div>
                      <div className="grid md:grid-cols-3 gap-4">
                        {solution.overall_strategy.attempt_order && (
                          <div className="bg-white rounded-lg p-3 border border-teal-200">
                            <p className="text-xs font-semibold text-teal-700">Attempt Order:</p>
                            <p className="text-sm text-slate-700">{solution.overall_strategy.attempt_order}</p>
                          </div>
                        )}
                        {solution.overall_strategy.time_allocation && (
                          <div className="bg-white rounded-lg p-3 border border-teal-200">
                            <p className="text-xs font-semibold text-teal-700">Time Split:</p>
                            <p className="text-sm text-slate-700">{solution.overall_strategy.time_allocation}</p>
                          </div>
                        )}
                        {solution.overall_strategy.pyq_pattern && (
                          <div className="bg-white rounded-lg p-3 border border-teal-200">
                            <p className="text-xs font-semibold text-teal-700">PYQ Pattern:</p>
                            <p className="text-sm text-slate-700">{solution.overall_strategy.pyq_pattern}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Topper Notes */}
                {solution.topper_notes && solution.topper_notes.length > 0 && (
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">💡</div>
                        <h3 className="text-lg font-bold text-blue-900 uppercase">Topper Notes for this Passage</h3>
                      </div>
                      <ul className="space-y-2">
                        {solution.topper_notes.map((note, idx) => (
                          <li key={idx} className="bg-white rounded-lg p-3 border border-blue-200 text-sm text-slate-800">
                            • {note}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </>
            )}

            {/* Single Question / Non-RC Display */}
            {!solution.is_multiple_questions && (
              <>
                {/* Enhanced Quant Display */}
                {(solution.shortcut_method || solution.detailed_method || solution.option_analysis || solution.data_analysis || solution.scenario_analysis) && (
                  <>
                    {/* Scenario Analysis (for Decision Making) */}
                    {solution.scenario_analysis && (
                      <Card className="border-violet-200 bg-gradient-to-br from-violet-50 to-purple-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-violet-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">⚖️</div>
                            <h3 className="text-lg font-bold text-violet-900 uppercase">Scenario Analysis</h3>
                            {solution.sub_type && (
                              <Badge className="bg-violet-100 text-violet-700 ml-auto">{solution.sub_type}</Badge>
                            )}
                          </div>
                          <div className="space-y-3">
                            {solution.scenario_analysis.situation_summary && (
                              <div className="bg-white rounded-lg p-4 border-2 border-violet-300">
                                <p className="text-violet-900">{solution.scenario_analysis.situation_summary}</p>
                              </div>
                            )}
                            {solution.scenario_analysis.decision_maker_role && (
                              <div className="flex items-center gap-2">
                                <Badge className="bg-violet-100 text-violet-800">Role: {solution.scenario_analysis.decision_maker_role}</Badge>
                                {solution.scenario_analysis.urgency_level && (
                                  <Badge className={`${
                                    solution.scenario_analysis.urgency_level === 'High' ? 'bg-red-100 text-red-700' :
                                    solution.scenario_analysis.urgency_level === 'Medium' ? 'bg-amber-100 text-amber-700' :
                                    'bg-green-100 text-green-700'
                                  }`}>
                                    Urgency: {solution.scenario_analysis.urgency_level}
                                  </Badge>
                                )}
                              </div>
                            )}
                            {solution.scenario_analysis.key_constraints && Array.isArray(solution.scenario_analysis.key_constraints) && (
                              <div className="bg-violet-100 rounded-lg p-4 border border-violet-300">
                                <p className="text-sm font-semibold text-violet-800 mb-2">⚠️ Key Constraints:</p>
                                <div className="flex flex-wrap gap-2">
                                  {solution.scenario_analysis.key_constraints.map((constraint, idx) => (
                                    <span key={idx} className="bg-white rounded px-3 py-1 text-sm text-violet-800 border border-violet-200">
                                      {constraint}
                                    </span>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Stakeholder Mapping (for Decision Making) */}
                    {solution.stakeholder_mapping && (
                      <Card className="border-rose-200 bg-gradient-to-br from-rose-50 to-pink-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-rose-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">👥</div>
                            <h3 className="text-lg font-bold text-rose-900 uppercase">Stakeholder Mapping</h3>
                          </div>
                          <div className="space-y-4">
                            {solution.stakeholder_mapping.primary_stakeholders && (
                              <div>
                                <p className="text-sm font-semibold text-rose-800 mb-2">Primary Stakeholders:</p>
                                <div className="grid md:grid-cols-2 gap-2">
                                  {solution.stakeholder_mapping.primary_stakeholders.map((s, idx) => (
                                    <div key={idx} className="bg-white rounded-lg p-3 border border-rose-200">
                                      <p className="font-medium text-rose-900">{s.name}</p>
                                      <p className="text-xs text-rose-700">Interest: {s.interest}</p>
                                      <div className="flex gap-2 mt-1">
                                        <Badge className="text-xs bg-rose-100 text-rose-700">Power: {s.power}</Badge>
                                        <Badge className="text-xs bg-rose-100 text-rose-700">Urgency: {s.urgency}</Badge>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            {solution.stakeholder_mapping.conflicting_interests && (
                              <div className="bg-red-50 rounded-lg p-3 border border-red-200">
                                <p className="text-sm text-red-800">
                                  <span className="font-semibold">⚠️ Conflicting Interests:</span> {solution.stakeholder_mapping.conflicting_interests}
                                </p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Decision Framework (for Decision Making) */}
                    {solution.decision_framework && (
                      <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-yellow-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-amber-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">🎯</div>
                            <h3 className="text-lg font-bold text-amber-900 uppercase">Decision Framework</h3>
                            {solution.decision_framework.framework_used && (
                              <Badge className="bg-amber-100 text-amber-700 ml-auto">{solution.decision_framework.framework_used}</Badge>
                            )}
                          </div>
                          <div className="space-y-3">
                            {solution.decision_framework.key_principles && (
                              <div className="bg-white rounded-lg p-4 border border-amber-200">
                                <p className="text-sm font-semibold text-amber-800 mb-2">Key Principles Applied:</p>
                                <ul className="space-y-1">
                                  {solution.decision_framework.key_principles.map((principle, idx) => (
                                    <li key={idx} className="text-sm text-amber-900 flex items-start gap-2">
                                      <span className="text-amber-500">✓</span> {principle}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            {solution.decision_framework.priority_order && (
                              <div className="bg-amber-100 rounded-lg p-3 border border-amber-300">
                                <p className="text-sm text-amber-900">
                                  <span className="font-semibold">Priority Order:</span> {solution.decision_framework.priority_order}
                                </p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Data Analysis (for Data Interpretation) */}
                    {solution.data_analysis && (
                      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-emerald-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">📊</div>
                            <h3 className="text-lg font-bold text-emerald-900 uppercase">Data Analysis</h3>
                            {solution.data_analysis.data_type && (
                              <Badge className="bg-emerald-100 text-emerald-700 ml-auto">{solution.data_analysis.data_type}</Badge>
                            )}
                          </div>
                          <div className="space-y-3">
                            {solution.data_analysis.relationships && (
                              <div className="bg-white rounded-lg p-4 border border-emerald-200">
                                <p className="text-sm font-semibold text-emerald-800 mb-1">📋 Data Relationships:</p>
                                <p className="text-slate-700">{solution.data_analysis.relationships}</p>
                              </div>
                            )}
                            {solution.data_analysis.key_values && Array.isArray(solution.data_analysis.key_values) && (
                              <div className="bg-emerald-100 rounded-lg p-4 border border-emerald-300">
                                <p className="text-sm font-semibold text-emerald-800 mb-2">🔢 Key Values Extracted:</p>
                                <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                                  {solution.data_analysis.key_values.map((val, idx) => (
                                    <div key={idx} className="bg-white rounded px-3 py-2 text-sm font-mono text-emerald-900 border border-emerald-200">
                                      {val}
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="flex gap-4 text-sm">
                              {solution.data_analysis.units && (
                                <div className="bg-white rounded px-3 py-2 border border-emerald-200">
                                  <span className="text-emerald-700 font-medium">Units:</span> {solution.data_analysis.units}
                                </div>
                              )}
                              {solution.data_analysis.time_period && (
                                <div className="bg-white rounded px-3 py-2 border border-emerald-200">
                                  <span className="text-emerald-700 font-medium">Period:</span> {solution.data_analysis.time_period}
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Pattern Identification (for Logical Reasoning) */}
                    {(solution.pattern_identified || solution.logic_rule_used) && (
                      <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-fuchsia-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">🧩</div>
                            <h3 className="text-lg font-bold text-purple-900 uppercase">Pattern Identified</h3>
                            {solution.sub_type && (
                              <Badge className="bg-purple-100 text-purple-700 ml-auto">{solution.sub_type}</Badge>
                            )}
                          </div>
                          <div className="space-y-3">
                            {solution.pattern_identified && (
                              <div className="bg-white rounded-lg p-4 border-2 border-purple-300">
                                <p className="text-purple-900 font-medium">{solution.pattern_identified}</p>
                              </div>
                            )}
                            {solution.logic_rule_used && (
                              <div className="bg-purple-100 rounded-lg p-4 border border-purple-300">
                                <p className="text-sm font-semibold text-purple-800 mb-1">📋 Logic Rule:</p>
                                <p className="text-purple-900">{solution.logic_rule_used}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Logic Visualization (for Logical Reasoning) */}
                    {solution.logic_visualization && (
                      <Card className="border-cyan-200 bg-gradient-to-br from-cyan-50 to-sky-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-cyan-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">📊</div>
                            <h3 className="text-lg font-bold text-cyan-900 uppercase">Logic Visualization</h3>
                            {solution.logic_visualization.type && (
                              <Badge className="bg-cyan-100 text-cyan-700 ml-auto capitalize">{solution.logic_visualization.type}</Badge>
                            )}
                          </div>
                          <div className="space-y-3">
                            {solution.logic_visualization.representation && (
                              <div className="bg-white rounded-lg p-4 border-2 border-cyan-300 font-mono text-center">
                                <pre className="text-cyan-900 whitespace-pre-wrap text-sm">{solution.logic_visualization.representation}</pre>
                              </div>
                            )}
                            {solution.logic_visualization.example && (
                              <div className="bg-cyan-100 rounded-lg p-3 border border-cyan-300">
                                <p className="text-sm text-cyan-800">
                                  <span className="font-semibold">💡 Reading:</span> {solution.logic_visualization.example}
                                </p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Concept & Formula (for Quant) */}
                    {(solution.concept_required || solution.formula_used) && !solution.pattern_identified && (
                      <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 to-blue-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-indigo-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">📐</div>
                            <h3 className="text-lg font-bold text-indigo-900 uppercase">Concept & Formula</h3>
                            {solution.sub_type && (
                              <Badge className="bg-indigo-100 text-indigo-700 ml-auto">{solution.sub_type}</Badge>
                            )}
                          </div>
                          <div className="space-y-3">
                            {solution.concept_required && (
                              <div className="bg-white rounded-lg p-4 border border-indigo-200">
                                <p className="text-sm font-semibold text-indigo-800 mb-1">Concept Required:</p>
                                <p className="text-slate-700">{solution.concept_required}</p>
                              </div>
                            )}
                            {solution.formula_used && (
                              <div className="bg-indigo-100 rounded-lg p-4 border border-indigo-300">
                                <p className="text-sm font-semibold text-indigo-800 mb-1">📝 Formula:</p>
                                <p className="text-indigo-900 font-mono text-lg">{solution.formula_used}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Shortcut Method (Highlighted) */}
                    {solution.shortcut_method && typeof solution.shortcut_method === 'object' && (
                      <Card className="border-green-300 bg-gradient-to-br from-green-50 to-emerald-50 shadow-md">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-green-600 text-white rounded-full w-10 h-10 flex items-center justify-center">
                              <Zap className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-green-900 uppercase">⚡ Shortcut Method</h3>
                              <p className="text-sm text-green-700">
                                {solution.shortcut_method.name || "Quick Method"} • {solution.shortcut_method.time || "30s"}
                              </p>
                            </div>
                            <Badge className="ml-auto bg-green-600 text-white">RECOMMENDED</Badge>
                          </div>
                          
                          <div className="space-y-3">
                            {solution.shortcut_method.steps && Array.isArray(solution.shortcut_method.steps) && (
                              <div className="bg-white rounded-lg p-4 border-2 border-green-300">
                                <ol className="space-y-2">
                                  {solution.shortcut_method.steps.map((step, idx) => (
                                    <li key={idx} className="flex items-start gap-3">
                                      <span className="bg-green-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold flex-shrink-0">
                                        {idx + 1}
                                      </span>
                                      <span className="text-slate-800">
                                        {typeof step === 'string' ? step : (typeof step === 'object' ? JSON.stringify(step) : String(step))}
                                      </span>
                                    </li>
                                  ))}
                                </ol>
                              </div>
                            )}
                            {solution.shortcut_method.explanation && (
                              <div className="bg-green-100 rounded-lg p-3 border border-green-300">
                                <p className="text-sm text-green-800">
                                  <span className="font-semibold">💡 Why this works:</span> {solution.shortcut_method.explanation}
                                </p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Detailed Method (Expandable) */}
                    {solution.detailed_method && typeof solution.detailed_method === 'object' && isPro && (
                      <Card className="border-slate-200 bg-slate-50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-slate-600 text-white rounded-full w-10 h-10 flex items-center justify-center">
                              <BookOpen className="w-5 h-5" />
                            </div>
                            <div>
                              <h3 className="text-lg font-bold text-slate-900 uppercase">📝 Detailed Step-by-Step</h3>
                              <p className="text-sm text-slate-600">
                                {solution.detailed_method.name || "Traditional Method"} • {solution.detailed_method.time || "90s"}
                              </p>
                            </div>
                          </div>
                          
                          <div className="space-y-4">
                            {solution.detailed_method.steps && Array.isArray(solution.detailed_method.steps) && solution.detailed_method.steps.map((step, idx) => (
                              <div key={idx} className="bg-white rounded-lg p-4 border border-slate-200">
                                <div className="flex items-center gap-2 mb-2">
                                  <span className="bg-slate-700 text-white rounded-full w-7 h-7 flex items-center justify-center text-sm font-bold">
                                    {(typeof step === 'object' ? step.step_number : null) || idx + 1}
                                  </span>
                                  <span className="font-semibold text-slate-800">
                                    {typeof step === 'string' ? step : (typeof step === 'object' ? (step.action || JSON.stringify(step)) : String(step))}
                                  </span>
                                </div>
                                {typeof step === 'object' && step.calculation && (
                                  <div className="ml-9 bg-blue-50 rounded p-3 border border-blue-200 font-mono text-blue-900">
                                    {step.calculation}
                                  </div>
                                )}
                                {typeof step === 'object' && step.result && (
                                  <div className="ml-9 mt-2 text-sm text-slate-600">
                                    <ArrowRight className="w-4 h-4 inline mr-1" />
                                    Result: <span className="font-semibold text-slate-800">{step.result}</span>
                                  </div>
                                )}
                              </div>
                            ))}
                            
                            {solution.detailed_method.final_calculation && (
                              <div className="bg-green-100 rounded-lg p-4 border-2 border-green-300">
                                <p className="text-sm font-semibold text-green-800 mb-1">Final Calculation:</p>
                                <p className="font-mono text-lg text-green-900">{solution.detailed_method.final_calculation}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Final Answer with Value */}
                    <Card className="border-green-300 bg-green-50">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">✓</div>
                          <h3 className="text-lg font-bold text-green-900 uppercase">Final Answer</h3>
                        </div>
                        <div className="flex items-center gap-4">
                          <div className="bg-white rounded-lg p-4 border-2 border-green-400 flex-1">
                            <p className="text-2xl font-bold text-green-900 text-center">
                              Option {solution.correct_answer}
                              {solution.answer_value && (
                                <span className="text-lg text-green-700 ml-2">= {solution.answer_value}</span>
                              )}
                            </p>
                          </div>
                        </div>
                        {/* Answer Explanation (for Logical Reasoning) */}
                        {solution.answer_explanation && (
                          <div className="mt-4 bg-green-100 rounded-lg p-4 border border-green-300">
                            <p className="text-sm text-green-800">{solution.answer_explanation}</p>
                          </div>
                        )}
                      </CardContent>
                    </Card>

                    {/* Option Analysis (Why each option exists) */}
                    {solution.option_analysis && isPro && (
                      <Card className="border-orange-200 bg-orange-50/50">
                        <CardContent className="p-6">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">🎯</div>
                            <h3 className="text-lg font-bold text-orange-900 uppercase">Option-by-Option Analysis</h3>
                          </div>
                          <div className="grid md:grid-cols-2 gap-3">
                            {Object.entries(solution.option_analysis).map(([opt, analysis]) => {
                              const isCorrect = analysis.status === "correct";
                              return (
                                <div 
                                  key={opt} 
                                  className={`rounded-lg p-4 border-2 ${
                                    isCorrect 
                                      ? "bg-green-50 border-green-300" 
                                      : "bg-red-50 border-red-200"
                                  }`}
                                >
                                  <div className="flex items-center gap-2 mb-2">
                                    {isCorrect ? (
                                      <CheckCircle className="w-5 h-5 text-green-600" />
                                    ) : (
                                      <XCircle className="w-5 h-5 text-red-600" />
                                    )}
                                    <span className={`font-bold text-lg ${isCorrect ? "text-green-700" : "text-red-700"}`}>
                                      Option {opt}
                                    </span>
                                    {analysis.value && (
                                      <span className="text-slate-600 font-mono">= {analysis.value}</span>
                                    )}
                                  </div>
                                  
                                  {isCorrect ? (
                                    <div className="space-y-2">
                                      {/* Decision Making: action_summary */}
                                      {analysis.action_summary && (
                                        <p className="text-sm text-green-800 italic mb-2">{analysis.action_summary}</p>
                                      )}
                                      {/* Decision Making: why_correct */}
                                      {analysis.why_correct && (
                                        <p className="text-sm text-green-800 bg-green-100 p-2 rounded">
                                          ✓ {analysis.why_correct}
                                        </p>
                                      )}
                                      {analysis.verification && (
                                        <p className="text-sm text-green-800 bg-green-100 p-2 rounded">
                                          ✓ {analysis.verification}
                                        </p>
                                      )}
                                      {/* Decision Making: strengths */}
                                      {analysis.strengths && Array.isArray(analysis.strengths) && (
                                        <div className="flex flex-wrap gap-1 mt-2">
                                          {analysis.strengths.map((s, idx) => (
                                            <Badge key={idx} className="bg-green-200 text-green-800 text-xs">{s}</Badge>
                                          ))}
                                        </div>
                                      )}
                                      {/* Decision Making: upsc_principle */}
                                      {analysis.upsc_principle && (
                                        <div className="bg-green-200 rounded p-2 border border-green-300 mt-2">
                                          <p className="text-xs font-semibold text-green-800">📋 UPSC Principle:</p>
                                          <p className="text-sm text-green-900">{analysis.upsc_principle}</p>
                                        </div>
                                      )}
                                      {/* DI-specific: calculation_shown for correct answer */}
                                      {analysis.calculation_shown && (
                                        <div className="bg-green-100 rounded p-2 border border-green-200">
                                          <p className="text-xs font-semibold text-green-700 mb-1">🔢 Calculation:</p>
                                          <p className="text-sm font-mono text-green-900">{analysis.calculation_shown}</p>
                                        </div>
                                      )}
                                    </div>
                                  ) : (
                                    <div className="space-y-2">
                                      {/* Decision Making: action_summary */}
                                      {analysis.action_summary && (
                                        <p className="text-sm text-red-700 italic mb-2">{analysis.action_summary}</p>
                                      )}
                                      {analysis.trap_type && (
                                        <Badge className="bg-red-100 text-red-700 text-xs">{analysis.trap_type}</Badge>
                                      )}
                                      {analysis.why_tempting && (
                                        <p className="text-sm text-red-800">
                                          <span className="font-semibold">Why tempting:</span> {analysis.why_tempting}
                                        </p>
                                      )}
                                      {/* Decision Making: critical_flaw */}
                                      {analysis.critical_flaw && (
                                        <p className="text-sm text-red-800">
                                          <span className="font-semibold">⚠️ Critical Flaw:</span> {analysis.critical_flaw}
                                        </p>
                                      )}
                                      {analysis.logical_flaw && (
                                        <p className="text-sm text-red-800">
                                          <span className="font-semibold">🧠 Logical Flaw:</span> {analysis.logical_flaw}
                                        </p>
                                      )}
                                      {/* Decision Making: ethical_issue */}
                                      {analysis.ethical_issue && (
                                        <p className="text-sm text-red-800">
                                          <span className="font-semibold">⚖️ Ethical Issue:</span> {analysis.ethical_issue}
                                        </p>
                                      )}
                                      {/* Decision Making: stakeholders_harmed */}
                                      {analysis.stakeholders_harmed && Array.isArray(analysis.stakeholders_harmed) && (
                                        <div className="bg-red-100 rounded p-2">
                                          <p className="text-xs font-semibold text-red-700">Stakeholders Harmed:</p>
                                          <p className="text-sm text-red-800">{analysis.stakeholders_harmed.join(', ')}</p>
                                        </div>
                                      )}
                                      {/* Decision Making: upsc_red_flag */}
                                      {analysis.upsc_red_flag && (
                                        <div className="bg-red-200 rounded p-2 border border-red-300 mt-2">
                                          <p className="text-xs font-semibold text-red-800">🚫 UPSC Red Flag:</p>
                                          <p className="text-sm text-red-900">{analysis.upsc_red_flag}</p>
                                        </div>
                                      )}
                                      {analysis.mistake_made && (
                                        <p className="text-sm text-red-700 bg-red-100 p-2 rounded">
                                          ⚠️ Mistake: {analysis.mistake_made}
                                        </p>
                                      )}
                                      {/* DI-specific: calculation_mistake */}
                                      {analysis.calculation_mistake && (
                                        <div className="bg-red-100 rounded p-2 border border-red-200 mt-2">
                                          <p className="text-xs font-semibold text-red-700 mb-1">🔢 Calculation Error:</p>
                                          <p className="text-sm font-mono text-red-900">{analysis.calculation_mistake}</p>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Verification Check */}
                    {solution.verification_check && isPro && (
                      <Card className="border-teal-200 bg-teal-50">
                        <CardContent className="p-4">
                          <div className="flex items-start gap-3">
                            <CheckCircle className="w-5 h-5 text-teal-600 mt-0.5" />
                            <div className="w-full">
                              <p className="font-bold text-teal-900 text-sm mb-2">Verification Check</p>
                              {/* Decision Making format - has legal_test, ethical_test etc */}
                              {solution.verification_check.legal_test && (
                                <div className="grid grid-cols-2 gap-2">
                                  <div className="bg-white rounded p-2 border border-teal-200">
                                    <p className="text-xs font-semibold text-teal-700">⚖️ Legal Test</p>
                                    <p className="text-xs text-teal-800">{solution.verification_check.legal_test}</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border border-teal-200">
                                    <p className="text-xs font-semibold text-teal-700">🎯 Ethical Test</p>
                                    <p className="text-xs text-teal-800">{solution.verification_check.ethical_test}</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border border-teal-200">
                                    <p className="text-xs font-semibold text-teal-700">✅ Practical Test</p>
                                    <p className="text-xs text-teal-800">{solution.verification_check.practical_test}</p>
                                  </div>
                                  <div className="bg-white rounded p-2 border border-teal-200">
                                    <p className="text-xs font-semibold text-teal-700">📊 Consequence Test</p>
                                    <p className="text-xs text-teal-800">{solution.verification_check.consequence_test}</p>
                                  </div>
                                </div>
                              )}
                              {/* Legacy format - has method and steps */}
                              {solution.verification_check.method && (
                                <>
                                  <p className="text-sm text-teal-800">{solution.verification_check.method}</p>
                                  {solution.verification_check.steps && (
                                    <ul className="mt-2 space-y-1">
                                      {solution.verification_check.steps.map((step, idx) => (
                                        <li key={idx} className="text-xs text-teal-700">• {step}</li>
                                      ))}
                                    </ul>
                                  )}
                                </>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* Ethical & Administrative Principles (for Decision Making) */}
                    {(solution.ethical_principles_applied || solution.administrative_principles_applied) && isPro && (
                      <div className="grid md:grid-cols-2 gap-4">
                        {solution.ethical_principles_applied && solution.ethical_principles_applied.length > 0 && (
                          <Card className="border-indigo-200 bg-indigo-50">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-lg">⚖️</span>
                                <p className="font-bold text-indigo-900 text-sm">Ethical Principles Applied</p>
                              </div>
                              <ul className="space-y-1">
                                {solution.ethical_principles_applied.map((principle, idx) => (
                                  <li key={idx} className="text-sm text-indigo-800 flex items-start gap-2">
                                    <span className="text-indigo-500">•</span> {principle}
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        )}
                        {solution.administrative_principles_applied && solution.administrative_principles_applied.length > 0 && (
                          <Card className="border-slate-200 bg-slate-50">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-2 mb-3">
                                <span className="text-lg">🏛️</span>
                                <p className="font-bold text-slate-900 text-sm">Administrative Principles Applied</p>
                              </div>
                              <ul className="space-y-1">
                                {solution.administrative_principles_applied.map((principle, idx) => (
                                  <li key={idx} className="text-sm text-slate-800 flex items-start gap-2">
                                    <span className="text-slate-500">•</span> {principle}
                                  </li>
                                ))}
                              </ul>
                            </CardContent>
                          </Card>
                        )}
                      </div>
                    )}

                    {/* Common Traps in Decision Making */}
                    {solution.common_traps_in_dm && solution.common_traps_in_dm.length > 0 && isPro && (
                      <Card className="border-orange-200 bg-orange-50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertCircle className="w-5 h-5 text-orange-600" />
                            <p className="font-bold text-orange-900 text-sm">Common Traps in Decision Making</p>
                          </div>
                          <ul className="space-y-1">
                            {solution.common_traps_in_dm.map((trap, idx) => (
                              <li key={idx} className="text-sm text-orange-800 flex items-start gap-2">
                                <span className="text-orange-500">⚠️</span> {trap}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {/* Common Mistakes */}
                    {solution.common_mistakes && solution.common_mistakes.length > 0 && isPro && (
                      <Card className="border-amber-200 bg-amber-50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <p className="font-bold text-amber-900 text-sm">Common Mistakes to Avoid</p>
                          </div>
                          <ul className="space-y-1">
                            {solution.common_mistakes.map((mistake, idx) => (
                              <li key={idx} className="text-sm text-amber-800 flex items-start gap-2">
                                <span className="text-amber-500">⚠️</span> {mistake}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {/* Calculation Tips (for DI) */}
                    {solution.calculation_tips && solution.calculation_tips.length > 0 && isPro && (
                      <Card className="border-sky-200 bg-sky-50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Calculator className="w-5 h-5 text-sky-600" />
                            <p className="font-bold text-sky-900 text-sm">Calculation Tips</p>
                          </div>
                          <ul className="space-y-1">
                            {solution.calculation_tips.map((tip, idx) => (
                              <li key={idx} className="text-sm text-sky-800 flex items-start gap-2">
                                <span className="text-sky-500">📌</span> {tip}
                              </li>
                            ))}
                          </ul>
                        </CardContent>
                      </Card>
                    )}

                    {/* DI-Specific Tips */}
                    {solution.di_specific_tips && isPro && (
                      <Card className="border-teal-200 bg-teal-50">
                        <CardContent className="p-4">
                          <div className="flex items-center gap-2 mb-3">
                            <Target className="w-5 h-5 text-teal-600" />
                            <p className="font-bold text-teal-900 text-sm">Data Type Specific Tips</p>
                          </div>
                          <div className="grid md:grid-cols-2 gap-2">
                            {Object.entries(solution.di_specific_tips).map(([type, tip], idx) => (
                              <div key={idx} className="bg-white rounded-lg p-3 border border-teal-200">
                                <p className="text-xs font-semibold text-teal-700 uppercase mb-1">{type.replace('_', ' ')}</p>
                                <p className="text-sm text-teal-800">{tip}</p>
                              </div>
                            ))}
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* PYQ Connection & Topper Notes */}
                    <div className="grid md:grid-cols-2 gap-4">
                      {(solution.pyq_connection || solution.pyq_pattern) && (
                        <Card className="border-purple-200 bg-purple-50">
                          <CardContent className="p-4">
                            <p className="font-bold text-purple-900 text-sm mb-1">📚 PYQ Pattern</p>
                            <p className="text-sm text-purple-800">{solution.pyq_connection || solution.pyq_pattern}</p>
                          </CardContent>
                        </Card>
                      )}
                      {solution.similar_question_tip && (
                        <Card className="border-blue-200 bg-blue-50">
                          <CardContent className="p-4">
                            <p className="font-bold text-blue-900 text-sm mb-1">💡 Similar Question Tip</p>
                            <p className="text-sm text-blue-800">{solution.similar_question_tip}</p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  </>
                )}

                {/* Passage Analysis for Single RC */}
                {solution.passage_analysis && (
                  <Card className="border-purple-200 bg-gradient-to-br from-purple-50 to-indigo-50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-purple-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">📖</div>
                        <h3 className="text-lg font-bold text-purple-900 uppercase">Passage Analysis</h3>
                      </div>
                      <div className="space-y-3">
                        <div className="bg-white rounded-lg p-4 border border-purple-200">
                          <p className="text-sm font-semibold text-purple-800 mb-1">Central Theme:</p>
                          <p className="text-slate-700">{solution.passage_analysis.central_theme}</p>
                        </div>
                        {solution.passage_analysis.key_points && (
                          <div className="bg-white rounded-lg p-4 border border-purple-200">
                            <p className="text-sm font-semibold text-purple-800 mb-2">Key Points:</p>
                            <ul className="space-y-1">
                              {solution.passage_analysis.key_points.map((point, idx) => (
                                <li key={idx} className="text-sm text-slate-700">• {point}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Final Answer - Only show if not enhanced quant */}
                {!solution.shortcut_method && !solution.detailed_method && !solution.option_analysis && (
                <Card className="border-green-200 bg-green-50">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="bg-green-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">✓</div>
                      <h3 className="text-lg font-bold text-green-900 uppercase">Final Correct Answer</h3>
                    </div>
                    <div className="bg-white rounded-lg p-4 border-2 border-green-300">
                      <p className="text-lg font-bold text-green-900">
                        {typeof solution.correct_answer === 'object' 
                          ? JSON.stringify(solution.correct_answer) 
                          : (solution.correct_answer || solution.answer || "See analysis below")}
                      </p>
                    </div>
                    {solution.solution_approach && (
                      <p className="mt-3 text-sm text-green-800">
                        {typeof solution.solution_approach === 'object'
                          ? JSON.stringify(solution.solution_approach)
                          : solution.solution_approach}
                      </p>
                    )}
                    {solution.passage_evidence && (
                      <div className="mt-3 p-3 bg-green-100 rounded-lg border border-green-300">
                        <p className="text-sm text-green-800">
                          <span className="font-semibold">📌 Passage Evidence:</span> {solution.passage_evidence}
                        </p>
                      </div>
                    )}
                  </CardContent>
                </Card>
                )}

                {/* Enhanced Elimination Analysis (for RC) */}
                {solution.elimination_analysis && isPro && !solution.option_analysis && (
                  <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">✗</div>
                        <h3 className="text-lg font-bold text-red-900 uppercase">Detailed Option Analysis</h3>
                      </div>
                      <div className="grid md:grid-cols-2 gap-3">
                        {Object.entries(solution.elimination_analysis).map(([opt, analysis]) => {
                          const isCorrect = analysis.status === "correct";
                          return (
                            <div 
                              key={opt} 
                              className={`rounded-lg p-4 border-2 ${
                                isCorrect 
                                  ? "bg-green-50 border-green-300" 
                                  : "bg-red-50 border-red-200"
                              }`}
                            >
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`font-bold ${isCorrect ? "text-green-700" : "text-red-700"}`}>
                                  {isCorrect ? "✓" : "✗"} Option {opt}
                                </span>
                                {analysis.trap_type && !isCorrect && (
                                  <Badge className="bg-red-100 text-red-700 text-xs">{analysis.trap_type}</Badge>
                                )}
                              </div>
                              <p className={`text-sm ${isCorrect ? "text-green-800" : "text-red-800"}`}>
                                {typeof analysis === 'string' ? analysis : analysis.reason}
                              </p>
                            </div>
                          );
                        })}
                      </div>
                    </CardContent>
                  </Card>
                )}

                {/* Legacy Elimination Steps (for non-enhanced responses) */}
                {!solution.option_analysis && !solution.elimination_analysis && (solution.elimination_logic || solution.elimination_steps) && isPro && (
                  <Card className="border-red-200 bg-red-50/50">
                    <CardContent className="p-6">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="bg-red-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">✗</div>
                        <h3 className="text-lg font-bold text-red-900 uppercase">Instant Elimination Logic</h3>
                      </div>
                      <div className="space-y-2">
                        {(solution.elimination_steps || solution.elimination_logic || []).map((item, idx) => (
                          <div key={idx} className="bg-white rounded-lg p-3 border border-red-200 flex items-start gap-2">
                            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-slate-800">
                              {typeof item === 'string' ? item : JSON.stringify(item)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </CardContent>
                  </Card>
                )}
            
            {/* Why Others Wrong (PRO) - Only show if no option_analysis */}
            {!solution.option_analysis && solution.why_others_wrong && Object.keys(solution.why_others_wrong).length > 0 && isPro && (
              <Card className="border-orange-200 bg-orange-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-orange-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">!</div>
                    <h3 className="text-lg font-bold text-orange-900 uppercase">Why Other Options Are Wrong</h3>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(solution.why_others_wrong).map(([opt, reason], idx) => (
                      <div key={idx} className="bg-white rounded-lg p-3 border border-orange-200">
                        <span className="font-bold text-orange-800">Option {opt}:</span>
                        <span className="text-sm text-slate-700 ml-2">
                          {typeof reason === 'string' ? reason : JSON.stringify(reason)}
                        </span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* PYQ Trap Alert (PRO) */}
            {solution.pyq_trap_alert && isPro && (
              <Card className="border-yellow-200 bg-yellow-50">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <span className="text-xl">⚠️</span>
                    <div>
                      <p className="font-bold text-yellow-900 text-sm">PYQ Trap Alert</p>
                      <p className="text-sm text-yellow-800">{solution.pyq_trap_alert}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Topper Notes (PRO) */}
            {solution.topper_notes && solution.topper_notes.length > 0 && isPro && (
              <Card className="border-blue-200 bg-blue-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-blue-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">💡</div>
                    <h3 className="text-lg font-bold text-blue-900 uppercase">Topper Notes</h3>
                  </div>
                  <ul className="space-y-2">
                    {solution.topper_notes.map((note, idx) => (
                      <li key={idx} className="bg-white rounded-lg p-3 border border-blue-200 text-sm text-slate-800">
                        • {note}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}

            {/* Fastest Method (PRO) - Only show for legacy simple string format */}
            {solution.shortcut_method && typeof solution.shortcut_method === 'string' && isPro && (
              <Card className="border-slate-200 bg-slate-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-slate-700 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">⚡</div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase">Fastest Exam-Hall Method</h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <p className="text-sm text-slate-800">{solution.shortcut_method}</p>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Step-by-step (PRO) */}
            {solution.step_by_step && solution.step_by_step.length > 0 && isPro && (
              <Card className="border-slate-200 bg-slate-50/50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="bg-slate-600 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold">📝</div>
                    <h3 className="text-lg font-bold text-slate-900 uppercase">Backup Method (Step-by-step)</h3>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-slate-200">
                    <ol className="list-decimal list-inside space-y-2">
                      {solution.step_by_step.map((step, idx) => (
                        <li key={idx} className="text-sm text-slate-800">{step}</li>
                      ))}
                    </ol>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Time & Difficulty */}
            {(solution.time_estimate || solution.difficulty || solution.should_attempt !== undefined) && (
              <div className="grid md:grid-cols-3 gap-4">
                {solution.time_estimate && (
                  <Card className="border-slate-200 bg-slate-50">
                    <CardContent className="p-4 text-center">
                      <Clock className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-xs font-medium text-slate-500 mb-1">TIME-TO-SOLVE</p>
                      <p className="text-lg font-bold text-slate-900">{solution.time_estimate}</p>
                    </CardContent>
                  </Card>
                )}
                {solution.difficulty && (
                  <Card className={`border-2 ${
                    solution.difficulty === 'Easy' ? 'border-green-300 bg-green-50' :
                    solution.difficulty === 'Medium' ? 'border-yellow-300 bg-yellow-50' :
                    'border-red-300 bg-red-50'
                  }`}>
                    <CardContent className="p-4 text-center">
                      <Target className={`w-8 h-8 mx-auto mb-2 ${
                        solution.difficulty === 'Easy' ? 'text-green-600' :
                        solution.difficulty === 'Medium' ? 'text-yellow-600' :
                        'text-red-600'
                      }`} />
                      <p className="text-xs font-medium mb-1">DIFFICULTY</p>
                      <p className="text-lg font-bold">{solution.difficulty}</p>
                    </CardContent>
                  </Card>
                )}
                {solution.should_attempt !== undefined && (
                  <Card className={`border-2 ${solution.should_attempt ? 'border-green-300 bg-green-50' : 'border-red-300 bg-red-50'}`}>
                    <CardContent className="p-4 text-center">
                      <Lightbulb className={`w-8 h-8 mx-auto mb-2 ${solution.should_attempt ? 'text-green-600' : 'text-red-600'}`} />
                      <p className="text-xs font-medium mb-1">SHOULD ATTEMPT?</p>
                      <p className="text-lg font-bold">{solution.should_attempt ? 'Yes ✓' : 'Skip ✗'}</p>
                    </CardContent>
                  </Card>
                )}
                {solution.attempt_strategy && (
                  <Card className="border-slate-200 bg-slate-50">
                    <CardContent className="p-4 text-center">
                      <Lightbulb className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                      <p className="text-xs font-medium text-slate-500 mb-1">ATTEMPT STRATEGY</p>
                      <p className="text-lg font-bold text-slate-900">{solution.attempt_strategy}</p>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}

            {/* PYQ Intelligence (PRO) */}
            {solution.pyq_analysis && isPro && (
              <Card className="border-slate-200 bg-slate-50">
                <CardContent className="p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <TrendingUp className="w-6 h-6 text-slate-600" />
                    <h3 className="text-lg font-bold text-slate-900">PYQ Intelligence</h3>
                  </div>
                  <div className="space-y-3">
                    {solution.pyq_analysis.frequency && (
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-medium text-slate-500 mb-1">PYQ FREQUENCY & TREND</p>
                        <p className="text-sm text-slate-800">{solution.pyq_analysis.frequency}</p>
                      </div>
                    )}
                    {solution.pyq_analysis.traps && (
                      <div className="bg-white rounded-lg p-3 border border-slate-200">
                        <p className="text-xs font-medium text-slate-500 mb-1">PYQ TRAP PATTERNS</p>
                        <p className="text-sm text-slate-800">{solution.pyq_analysis.traps}</p>
                      </div>
                    )}
                    {solution.pyq_analysis.safety_signal && (
                      <div className={`rounded-lg p-3 border ${PYQ_SIGNALS[solution.pyq_analysis.safety_signal]?.color || 'bg-slate-100'}`}>
                        <p className="text-sm font-semibold">
                          {PYQ_SIGNALS[solution.pyq_analysis.safety_signal]?.icon} {PYQ_SIGNALS[solution.pyq_analysis.safety_signal]?.label}
                        </p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Paywall */}
            {solution.paywall_message && (
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 text-center">
                <Calculator className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">Upgrade to PRO</h3>
                <p className="text-sm text-slate-600">{solution.paywall_message}</p>
              </div>
            )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default CSATQuestionSolver;
